/**
 * The three shapes the published pages give a list of ticks. Which one a
 * section takes is read off the fields it carries, since the data names no
 * variant for this kind:
 *
 * - `highlight`: `.aff_benefits-box`, the affiliate and referral commission card.
 * - `lead`: `.y-p_integrate-box`, the mint panel with its ticks in one row.
 * - neither: `.benefits_list.is-3`, a row of icon tiles under a centred heading.
 *
 * Both of the last two publish on `/youtube-proxy`, one with a line under its
 * heading and one without, which is the difference the published markup turns
 * on as well.
 */
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Fragment } from "react";

import { CheckIcon } from "@/components/marketing/MarketingIcons";
import { HEADING_2 } from "@/components/marketing/marketingType";
import { SECTION_WASH } from "@/components/marketing/sectionPanel";
import {
  ACCENT,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { LandingImage, LandingSection } from "../LandingPrimitives";
import { PlainBandHeading, CheckRow, SHALLOW_FOOT } from "./landingBands";
import { Lines } from "@/components/marketing/Lines";

type CheckList = Laid<"checkList">;

/** `.aff_benefits-box`: the wash the "The Benefits" card floats on. */
const BENEFITS_BOX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "40px",
  p: "48px",
  borderRadius: "32px",
  backgroundImage: SECTION_WASH.wide,
  [NOT_WIDE]: { gap: "32px", p: "32px" },
  [MID]: { backgroundImage: SECTION_WASH.narrow },
  [TINY]: { gap: "16px", p: 0, borderRadius: "24px", backgroundImage: "none" },
};

/** `.aff_benefits-contain`: the card the wash is there to lift. */
const BENEFITS_CARD = {
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  width: 1,
  p: "30.08px",
  borderRadius: "24px",
  backgroundColor: "common.white",
  boxShadow: "0 12px 32px -10px rgba(154, 161, 169, 0.26)",
  [NOT_WIDE]: { gap: "24px", p: "19.2px" },
  // With no gradient behind it, the card needs its own rule to read as a card.
  [TINY]: { gap: "16px", border: `1px solid ${marketingPalette.wash}` },
};

/** `.aff_benefits-comission`: the banner the card opens on. */
const COMMISSION = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  p: "20px",
  borderRadius: "22.08px",
  backgroundImage: "linear-gradient(98deg, #F7FFE0, #DBFFE7)",
  [NOT_WIDE]: { p: "12.8px" },
  [MID]: { flexDirection: "column" },
  [TINY]: { p: "9.6px", borderRadius: "16px" },
};

const COMMISSION_TEXT = {
  color: "common.black",
  fontSize: 24,
  lineHeight: "28.8px",
  fontWeight: 400,
  [MID]: { fontSize: 19.2, lineHeight: "23.04px" },
};

/** `.aff_benefits-bottom_wrap`: three ticks abreast, one row under the other. */
const BENEFIT_ROW = {
  display: "flex",
  alignItems: "center",
  gap: "24px",
  [MID]: { flexDirection: "column" },
};

const BENEFIT_ITEM = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  width: 1,
  color: marketingPalette.ink,
  fontSize: 14.4,
  lineHeight: "20.16px",
  fontWeight: 500,
};

const BENEFITS_PER_ROW = 3;

/** `.container_new.padding-bottom`: the short close the earn pages set their panel on. */
const BREAK_FOOT = {
  pb: "48px",
  [NOT_WIDE]: { pb: "64px" },
  [TINY]: { pb: "48px" },
};

function CommissionBanner({ block }: { block: CheckList }) {
  if (!block.lead && !block.highlight) {
    return null;
  }
  return (
    <Box sx={COMMISSION}>
      {block.lead ? (
        <Typography sx={COMMISSION_TEXT}>{block.lead}</Typography>
      ) : null}
      {block.highlight && block.highlight !== block.lead ? (
        <Typography sx={[COMMISSION_TEXT, { fontWeight: 600 }]}>
          {block.highlight}
        </Typography>
      ) : null}
    </Box>
  );
}

function BenefitsPanel({ block }: { block: CheckList }) {
  const rows: Array<CheckList["checks"]> = [];
  for (let at = 0; at < block.checks.length; at += BENEFITS_PER_ROW) {
    rows.push(block.checks.slice(at, at + BENEFITS_PER_ROW));
  }
  return (
    <LandingSection
      padding={block.pad === "break" ? "none" : "bottom"}
      sx={block.pad === "break" ? BREAK_FOOT : undefined}
    >
      <Box sx={BENEFITS_BOX}>
        {block.title ? (
          <Typography component="h2" sx={HEADING_2}>
            <Lines text={block.title} />
          </Typography>
        ) : null}
        <Box sx={BENEFITS_CARD}>
          <CommissionBanner block={block} />
          {rows.map((row, rowIndex) => (
            <Fragment key={rowIndex}>
              {rowIndex > 0 ? (
                <Box
                  aria-hidden
                  sx={{
                    width: 1,
                    height: "0.96px",
                    backgroundColor: marketingPalette.outline,
                  }}
                />
              ) : null}
              <Box sx={BENEFIT_ROW}>
                {row.map((check, index) => (
                  <Box key={index} sx={BENEFIT_ITEM}>
                    {check.icon ? (
                      <LandingImage
                        src={check.icon}
                        sx={{ width: "24px", height: "24px", flexShrink: 0 }}
                      />
                    ) : (
                      <Box sx={{ display: "flex", color: ACCENT }}>
                        <CheckIcon size={24} />
                      </Box>
                    )}
                    {check.label}
                  </Box>
                ))}
              </Box>
            </Fragment>
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}

/** `.y-p_integrate-box`: the mint panel, its heading and line centred over one row of ticks. */
const INTEGRATE_BOX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "24px",
  p: "48px",
  borderRadius: "32px",
  // The published panel's own mint; no palette role carries this one.
  backgroundColor: "#F4FBFA",
  overflow: "hidden",
  [NOT_WIDE]: { p: "32px 32px 48px" },
  [MID]: { p: "32px 16px 48px", borderRadius: "16px" },
  [TINY]: { p: "32px 16px" },
};

function IntegratePanel({ block }: { block: CheckList }) {
  return (
    <LandingSection padding="bottom">
      <Box sx={INTEGRATE_BOX}>
        <PlainBandHeading title={block.title} body={block.lead} />
        <CheckRow checks={block.checks} iconSize={24} />
      </Box>
    </LandingSection>
  );
}

/** `.benefits_wrap`: the centred heading and the tiles under it. */
const BENEFIT_CARDS_WRAP = {
  display: "flex",
  flexDirection: "column",
  gap: "40px",
  [MID]: { gap: "32px" },
  [TINY]: { gap: "24px" },
};

/**
 * `.benefits_list.is-3`: three tiles abreast on the grid, sharing one flex row
 * below the wide step and stacking below the card step.
 */
const BENEFIT_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "20px",
  [NOT_WIDE]: { display: "flex" },
  [MID]: { flexDirection: "column", gap: "16px" },
};

/** `.benefits_item.is-1`: the lilac wash inside a near-white rule, opaque below the card step. */
const BENEFIT_CARD = {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  p: "24px",
  borderRadius: "16px",
  border: `1px solid ${marketingPalette.plainCardRule}`,
  backgroundColor: marketingPalette.plainCard,
  // Sized by content rather than by the column once the grid becomes a row.
  [NOT_WIDE]: { flex: "1 1 auto" },
  [MID]: { backgroundColor: marketingPalette.surface },
  [TINY]: { gap: "16px", p: "16px" },
};

const BENEFIT_CARD_ICON = {
  width: "48px",
  height: "48px",
  flexShrink: 0,
  [TINY]: { width: "32px", height: "32px" },
};

/** `.benefits_title`: a size over body copy and set on its own leading. */
const BENEFIT_CARD_TITLE = {
  color: marketingPalette.ink,
  fontSize: 18,
  lineHeight: "27px",
  fontWeight: 500,
};

function BenefitCards({ block }: { block: CheckList }) {
  return (
    <LandingSection padding="none" sx={SHALLOW_FOOT}>
      <Box sx={BENEFIT_CARDS_WRAP}>
        <PlainBandHeading title={block.title} sx={{ width: 1 }} />
        <Box sx={BENEFIT_GRID}>
          {block.checks.map((check, index) => (
            <Box key={index} sx={BENEFIT_CARD}>
              {check.icon ? (
                <LandingImage src={check.icon} sx={BENEFIT_CARD_ICON} />
              ) : (
                <Box aria-hidden sx={BENEFIT_CARD_ICON} />
              )}
              <Typography sx={BENEFIT_CARD_TITLE}>{check.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}

/** The commission card, the mint panel and the icon tiles, one per published shape. */
export function CheckListBlock({ block }: { block: CheckList }) {
  if (block.highlight) {
    return <BenefitsPanel block={block} />;
  }
  if (block.lead) {
    return <IntegratePanel block={block} />;
  }
  return <BenefitCards block={block} />;
}
